import type { ComponentType } from 'react'
import type { Locale } from '@/shared/i18n'

export interface BlogFaqItem {
  q: string
  a: string
}

interface BlogFrontmatter {
  title: string
  description: string
  slug?: string
  locale: Locale
  date: string
  updated?: string
  tags?: string[]
  ogImage?: string
  relatedTools?: string[]
  faq?: BlogFaqItem[]
  /** Map of other-locale slugs pointing at this post's translation in that locale. */
  translations?: Partial<Record<Locale, string>>
}

interface MdxModule {
  default: ComponentType
  frontmatter: BlogFrontmatter
}

export interface BlogPost {
  /** Slug derived from filename (without `.mdx`). Authoritative URL segment. */
  slug: string
  locale: Locale
  title: string
  description: string
  date: string
  updated?: string
  tags: string[]
  relatedTools: string[]
  ogImage?: string
  faq: BlogFaqItem[]
  translations: Partial<Record<Locale, string>>
  Component: ComponentType
}

// Eager glob — every post is bundled and discoverable at build time.
// `import.meta.glob` matches relative paths only — keep paths in sync if
// the directory layout changes.
// Keep the Vite glob in the production/SSG branch so Vite transforms it into
// the complete registry. Plain tsx tests intentionally use an isolated empty
// fixture branch; they do not exercise blog discovery and cannot provide the
// Vite-only import.meta.glob API.
const modules = process.env.NODE_ENV === 'test'
  ? {}
  : import.meta.glob<MdxModule>('../../content/blog/*.mdx', { eager: true })

function slugFromPath(path: string): string {
  const file = path.split('/').pop() ?? path
  return file.replace(/\.mdx$/, '')
}

const POSTS: BlogPost[] = Object.entries(modules)
  .map(([path, mod]) => {
    const fm = mod.frontmatter
    if (
      !fm ||
      typeof fm.title !== 'string' ||
      typeof fm.date !== 'string' ||
      typeof fm.locale !== 'string'
    ) {
      throw new Error(
        `Blog post ${path} is missing required frontmatter (title, date, locale)`,
      )
    }
    return {
      slug: fm.slug ?? slugFromPath(path),
      locale: fm.locale,
      title: fm.title,
      description: fm.description,
      date: fm.date,
      updated: fm.updated,
      tags: fm.tags ?? [],
      relatedTools: fm.relatedTools ?? [],
      ogImage: fm.ogImage,
      faq: fm.faq ?? [],
      translations: fm.translations ?? {},
      Component: mod.default,
    }
  })
  // Newest first
  .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))

export function getAllPosts(locale?: Locale): BlogPost[] {
  return locale ? POSTS.filter((p) => p.locale === locale) : POSTS
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return POSTS.find((p) => p.slug === slug)
}
